import { projectNebulaTitleBoxes, setNebulaTime } from '../neuralMaterials';
import { applyHullReveal, writeRevealedPositions } from '../nestReveal';
import { createFrameOverlays } from './overlays';
import { collectVisibleLabelBoxes } from './labelSlots';
import { stepCamera } from './stepCamera';
import { followNestMotion, stepEdges } from './stepEdges';
import { stepNodeLabels } from './stepLabels';
import { wantsClearanceSolve } from './idleClearance';
import { cullExitingMeshes, stepHalos, stepNodePresentation } from './stepNodes';

/**
 * One animation frame: graph sync, camera/zoom, node/edge/label updates, HTML card, renders.
 */
export function runNeuralAnimationFrame({
  graph,
  camera,
  controls,
  renderer,
  composer,
  ignition,
  now,
  delta,
  reducedMotion,
  labelRenderer,
  scene,
  container,
  floatingCardWrapRef,
  hoverNameEl,
  hoverNameText,
  raycaster,
  INITIAL_DIST,
  MIN_DIST,
  MAX_DIST,
  ZOOM_DAMPING,
  targetDistRef,
  focusTargetGoalRef,
  focusCameraGoalRef,
  pulseTRef,
  driftRef,
  interactionRef,
  FOCUS_TARGET_LERP,
  FOCUS_CAM_LERP,
  FOCUS_SNAP_EPS,
  zoomCursorRef,
  zoomDriveRef,
  ZOOM_FRICTION,
}) {
  const pulseT = pulseTRef.v + (reducedMotion ? 0 : delta);
  pulseTRef.v = pulseT;

  const {
    meshByTaskId,
    haloByTaskId,
    labelByTaskId,
    edgeLinesByKey,
    selectionRing,
    hoverRing,
    ensureGraphSynced,
    rebuildRaycastTargets,
    placeLabelNearMesh,
    mountLabel,
    updateEdgeCurveGeometry,
    refreshOccluders,
    markOccludersDirty,
    finishOccluderClip,
    removeHalo,
    detachLabelForTask,
    disposeMissionMesh,
    getSelectedTaskId,
    constants: {
      NEURAL_TRANS_POS_LERP,
      NEURAL_LAYOUT_JUMP_LERP,
      NEURAL_TRANS_FADE_LERP,
      NEURAL_TRANS_EDGE_FADE_LERP,
    },
  } = graph;

  const selectedTaskIdLocal = getSelectedTaskId();
  const hoverPointerId = raycaster.hoverPointerId;

  ensureGraphSynced();
  const { zooming } = stepCamera({
    camera,
    controls,
    delta,
    now,
    reducedMotion,
    targetDistRef,
    focusTargetGoalRef,
    focusCameraGoalRef,
    driftRef,
    interactionRef,
    FOCUS_TARGET_LERP,
    FOCUS_CAM_LERP,
    FOCUS_SNAP_EPS,
    ZOOM_DAMPING,
    zoomCursorRef,
    zoomDriveRef,
    MIN_DIST,
    MAX_DIST,
    ZOOM_FRICTION,
  });

  const dragging = !!interactionRef?.dragging;
  const framing = !!(focusTargetGoalRef?.v && focusCameraGoalRef?.v);
  if (zooming) interactionRef.lastZoomAt = now;
  const settleArrange = now - (interactionRef?.lastZoomAt ?? 0) < 450;
  const lastManual = Math.max(
    interactionRef?.lastZoomAt ?? 0,
    interactionRef?.lastAt ?? 0,
  );
  const manualClear = !!(dragging || zooming || framing || now - lastManual < 520);
  const arranging = !!(zooming || settleArrange);
  // Idle orbit still eases seats every frame. The overlap search itself is throttled.
  const freezeArrange = false;
  const distScale = Math.max(0.5, camera.position.distanceTo(controls.target) / INITIAL_DIST);
  if (arranging || graph.layoutDist == null || Math.abs(distScale - graph.layoutDist) > 0.08) {
    graph.layoutDist = distScale;
  }
  const layoutDist = graph.layoutDist;
  const { worldPos, projectForCard, tmpVb, tmpVa } = raycaster.vectors;
  const overlays = createFrameOverlays({
    camera,
    meshByTaskId,
    selectionRing,
    hoverRing,
    hoverNameEl,
    hoverNameText,
    labelByTaskId,
    selectedTaskIdLocal,
    hoverPointerId,
    nebula: graph.nebula,
    distScale,
    floatingCardWrapRef,
    container,
    raycaster,
    labelRenderer,
    composer,
    renderer,
    scene,
    delta,
  });

  if (dragging) {
    if (interactionRef) interactionRef.idleClearAt = null;
    const { moved } = writeRevealedPositions(
      meshByTaskId,
      camera,
      delta,
      reducedMotion,
      graph.nebula,
      container,
      false,
      labelByTaskId,
    );
    applyHullReveal();
    if (moved.size) markOccludersDirty?.();
    refreshOccluders?.(camera);
    followNestMotion({
      movedIds: moved,
      edgeLinesByKey,
      meshByTaskId,
      haloByTaskId,
      labelByTaskId,
      placeLabelNearMesh,
      updateEdgeCurveGeometry,
      camera,
      tmpVa,
      tmpVb,
      zooming,
    });
    finishOccluderClip?.();
    if (graph.nebula?.visible) setNebulaTime(graph.nebula, pulseT, camera, meshByTaskId, container, edgeLinesByKey, true);
    graph.syncNodeInstances?.(camera, pulseT);
    overlays.updateSelectionRing();
    overlays.updateHoverChrome();
    overlays.updateFloatingCard();
    overlays.paint();
    return;
  }

  const igniting = ignition.getEvent?.() != null;
  const { anyMoving: nodesMoving, movedIds, meshesToCull } = stepNodePresentation({
    meshByTaskId,
    labelByTaskId,
    placeLabelNearMesh,
    selectedTaskIdLocal,
    hoverPointerId,
    distScale,
    layoutDist,
    pulseT,
    reducedMotion,
    delta,
    igniting,
    NEURAL_TRANS_POS_LERP,
    NEURAL_LAYOUT_JUMP_LERP,
    NEURAL_TRANS_FADE_LERP,
  });
  let anyMoving = nodesMoving;

  const solveClearance = !interactionRef || wantsClearanceSolve({
    manualClear,
    nodesMoving,
    igniting,
    now,
    clock: interactionRef,
  });
  const revealed = writeRevealedPositions(
    meshByTaskId,
    camera,
    delta,
    reducedMotion,
    graph.nebula,
    container,
    freezeArrange,
    labelByTaskId,
    manualClear,
    !solveClearance,
  );
  revealed.moved.forEach((id) => movedIds.add(id));
  if (revealed.moved.size) anyMoving = true;
  if (anyMoving) markOccludersDirty?.();
  refreshOccluders?.(camera);
  if (graph.nebula?.visible) {
    const nameBoxes = collectVisibleLabelBoxes(labelByTaskId, camera, container, meshByTaskId);
    const huntTitles = !!(dragging || zooming || now - (interactionRef?.lastZoomAt ?? 0) < 180);
    setNebulaTime(graph.nebula, pulseT, camera, meshByTaskId, container, edgeLinesByKey, false, nameBoxes, !huntTitles);
  }

  cullExitingMeshes({
    meshesToCull,
    meshByTaskId,
    labelByTaskId,
    removeHalo,
    detachLabelForTask,
    disposeMissionMesh,
  });

  stepHalos({
    haloByTaskId,
    selectedTaskIdLocal,
    pulseT,
    reducedMotion,
    igniting,
    anyMoving,
    delta,
    removeHalo,
  });

  stepEdges({
    edgeLinesByKey,
    meshByTaskId,
    camera,
    tmpVa,
    tmpVb,
    updateEdgeCurveGeometry,
    finishOccluderClip,
    nodeGroup: graph.nodeGroup,
    igniting,
    dragging,
    NEURAL_TRANS_EDGE_FADE_LERP,
    forceHigh: zooming || settleArrange,
  });
  graph.updateHulls?.(NEURAL_TRANS_POS_LERP);
  applyHullReveal();

  ignition.update(now, camera, renderer.getPixelRatio(), reducedMotion);

  if (anyMoving) {
    graph.nodeGroup.updateMatrixWorld(true);
    rebuildRaycastTargets();
  }

  graph.syncNodeInstances?.(camera, pulseT);
  overlays.updateSelectionRing();
  stepNodeLabels({
    labelByTaskId,
    meshByTaskId,
    camera,
    container,
    now,
    selectedTaskIdLocal,
    MIN_DIST,
    MAX_DIST,
    worldPos,
    projectForCard,
    placeLabelNearMesh,
    mountLabel,
    freezeSet: freezeArrange,
    hoverPointerId,
    manualClear,
    extraBoxes: graph.nebula?.visible
      ? projectNebulaTitleBoxes(graph.nebula, camera, container)
      : null,
  });
  overlays.updateHoverChrome();
  overlays.updateFloatingCard();
  overlays.paint();
}
